
import Swiper from 'swiper';
import generateIcon from 'Models/icon';
import { dispatch, listen } from 'Models/utils/event-bus';
import store from 'Store';

export default function slider (element) {
    let slider;
    let DOMPrepared = false;

    const baseSliderSettings = {
        slidesPerView: 'auto',
        autoHeight: true,
        navigation: false, // if you want a custom / outside navigation, use an object with prevEl, nextEl (which should be classnames) and custom: true
        pagination: false,
        initialSlide: 0,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        watchOverflow: true,
        centerInsufficientSlides: true,
        customLoop: false, // will slide to first slide when clicking nvest navigation button on last slide
        navigationColor: 'light', // either light or dark
        navigationIcon: 'button',
        until: false, // deactivate slider on screens larger than this breakpoint
        // from: false,
        paginationType: 'bullets', // either bullets, fraction, custom (this one requires a data-caption on the slides)
        controlling: false, // name of the slider to remote control
        name: null, // name to identify slider (i.e. to allow remote control)
        updateOnEvent: null, // init listener to this event to update slider
        stayInitialized: false, // avoid not initializing slider for search
        slideToListener: false, // listens to event to slide to certain slide
    };

    const customSliderSettings = JSON.parse(element.dataset.sliderSettings);

    const sliderSettings = { ...baseSliderSettings, ...customSliderSettings };

    const hasMultipleSlides = element.children.length > 1;

    const slideClassname = sliderSettings.slideClass ? sliderSettings.slideClass : 'swiper-slide';

    let activeBreakpoints = store.getters.getActiveBreakpoints;

    let controlledSwiper;

    const hasVideoInside = element.querySelectorAll('video').length > 0;
    let videoLoaded = false;

    function init () {
        if (hasMultipleSlides || sliderSettings.stayInitialized) {
            initSwiper();
            addResizeListener();
        } else {
            if (hasVideoInside) {
                listen('videoInSliderLoaded', videoLoadedEvent);
                const i = setInterval(() => {
                    dispatch('playVideoInSlider', { element });
                    if (videoLoaded) clearInterval(i);
                }, 100);
            }
        }
    }

    function videoLoadedEvent (e) {
        if (e.element === element) {
            videoLoaded = true;
        }
    }

    function initSwiper () {
        if (slider !== undefined) return;

        if (!DOMPrepared) {
            prepareDOM();
        }

        let navigationPrevEl;
        let navigationNextEl;

        // navigation
        if (sliderSettings.navigation) {
            if (sliderSettings.navigation.custom) {
                navigationPrevEl = document.querySelector(sliderSettings.navigation.prevEl);
                navigationNextEl = document.querySelector(sliderSettings.navigation.nextEl);
            } else {
                navigationPrevEl = element.querySelectorAll('.slider-prev')[0];
                navigationNextEl = element.querySelectorAll('.slider-next')[0];
            }
            sliderSettings.navigation = {};
            sliderSettings.navigation.prevEl = navigationPrevEl;
            sliderSettings.navigation.nextEl = navigationNextEl;
        }

        // pagination
        if (sliderSettings.pagination) {
            sliderSettings.pagination = {
                el: element.querySelectorAll('.slider-pagination')[0],
                clickable: true,
                type: sliderSettings.paginationType,
            };

            // adds custom pagination with text from data-caption on active slide
            if (sliderSettings.paginationType === 'custom') {
                sliderSettings.pagination.renderCustom = function (swiper, current) {
                    const activeSlide = swiper.$el[0].querySelectorAll(`.${slideClassname}`)[current - 1];
                    const caption = activeSlide.dataset.caption;
                    return caption;
                };
            }
        }

        // controller
        if (sliderSettings.controlling) {
            if (controlledSwiper === undefined) {
                listen('sliderInitialized', payload => {
                    enableRemoteControl(payload);
                });
            } else {
                sliderSettings.controller = {
                    control: controlledSwiper,
                };
            }
        }

        // slideto listener
        if (sliderSettings.slideToListener) {
            listen(sliderSettings.slideToListener, function (e) {
                if (slider) {
                    slider.update();
                    slider.slideTo(e, 0);
                }
            });
        }

        sliderSettings.on = {
            init: () => {
                setTimeout(() => {
                    dispatch('sliderInitialized', { element, name: sliderSettings.name, slider });
                }, 10);
            },
            slideChangeTransitionEnd: () => {
                dispatch('slideChangeEnd', { element });
            },
        };

        // slide to first slide if next button on last slide is clicked – avoids issue with duplicated video not playing
        if (sliderSettings.customLoop && navigationPrevEl && navigationNextEl) {
            navigationPrevEl.addEventListener('click', slideToLast);
            navigationNextEl.addEventListener('click', slideToFirst);
        }

        if (sliderSettings.updateOnEvent) {
            listen(sliderSettings.updateOnEvent, updateSwiper);
        }

        slider = new Swiper(element, sliderSettings);
    }

    function prepareDOM () {
        element.classList.add('swiper-container');
        if (element.querySelectorAll('.swiper-wrapper').length === 0) {
            const slides = element.children;
            const wrapper = document.createElement('div');
            wrapper.classList.add('swiper-wrapper');
            [...slides].forEach(slide => {
                slide.classList.add(slideClassname);
                wrapper.appendChild(slide);
            });
            element.appendChild(wrapper);
        }

        if (sliderSettings.pagination || sliderSettings.navigation) {
            if (!element.querySelector('.slider-navigation') && !sliderSettings.navigation.custom) {
                const navigationElement = document.createRange().createContextualFragment(createNavigation(sliderSettings.pagination, sliderSettings.navigation));
                element.appendChild(navigationElement);
            }
        }

        DOMPrepared = true;
    }

    function slideToFirst () {
        if (slider.isEnd) {
            setTimeout(() => {
                slider.slideTo(0);
            }, 10);
        }
    }
    function slideToLast () {
        if (slider.isBeginning) {
            setTimeout(() => {
                slider.slideTo(element.children.length - 1);
            }, 10);
        }
    }

    function createNavigation (hasPagination, hasNavigation) {
        return `
            <div class="slider-navigation slider-navigation--${sliderSettings.navigationColor}">
                ${hasNavigation ? `<div class="slider-navigation__arrow slider-prev">${generateIcon(sliderSettings.navigationIcon + '-left', true)}</div>` : ''}
                ${hasPagination ? '<div class="slider-navigation__pagination slider-pagination"></div>' : ''}
                ${hasNavigation ? `<div class="slider-navigation__arrow slider-next">${generateIcon(sliderSettings.navigationIcon + '-right', true)}</div>` : ''}
            </div>
        `;
    }

    function addResizeListener () {
        store.watch((state) => {
            activeBreakpoints = state.activeBreakpoints;
            toggleSlider();
        });
    }

    function toggleSlider () {
        if (sliderSettings.until && activeBreakpoints.length) {
            if (activeBreakpoints.indexOf(sliderSettings.until) < 0) {
                initSwiper();
                slider.update();
            } else {
                destroySwiper();
            }
        }
    }

    function destroySwiper () {
        if (slider !== undefined) {
            unprepareDOM();
            slider.destroy();
            slider = undefined;
        }
    }

    function resetSwiper () {
        if (slider !== undefined) {
            slider.destroy();
            slider = undefined;
        }
        initSwiper();
    }

    function updateSwiper () {
        if (slider !== undefined) {
            setTimeout(() => {
                slider.update();
            }, 10);
        }
    }

    function unprepareDOM () {
        element.classList.remove('swiper-container');
        const wrapper = element.querySelectorAll('.swiper-wrapper')[0];
        const navigation = element.querySelectorAll('.slider-navigation')[0];
        const slides = wrapper.querySelectorAll(`.${slideClassname}:not(.${slideClassname}-duplicate)`);
        element.removeChild(wrapper);
        element.removeChild(navigation);

        [...slides].forEach(slide => {
            slide.classList.remove(slideClassname);
            element.appendChild(slide);
        });

        DOMPrepared = false;
    }

    function enableRemoteControl (payload) {
        if (payload.name === sliderSettings.controlling) {
            controlledSwiper = payload.slider;
            resetSwiper();
        }
    }

    init();
}
